import React from 'react';
import { cn } from '@utils/cn';
import { TaskMaterialStatus } from '@models/warehouse.model';
import { useTranslation } from '@hooks/useTranslation';

interface TaskMaterialStatusBadgeProps {
  status: TaskMaterialStatus;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs h-5',
  md: 'px-3 py-1 text-sm h-6',
  lg: 'px-4 py-1.5 text-base h-8',
};

export const TaskMaterialStatusBadge: React.FC<TaskMaterialStatusBadgeProps> = ({
  status,
  size = 'sm',
  className,
}) => {
  // Sprint 29, story-148. Colors unchanged — text-only fix; `statusConfig` moved inside the
  // component body since it now needs the translation hook.
  const tEnumLabels = useTranslation('enumLabels');
  const statusConfig: Record<
    TaskMaterialStatus,
    { label: string; classes: string; dotColor: string }
  > = {
    [TaskMaterialStatus.PLANNED]: {
      label: tEnumLabels.taskMaterialStatusPlanned,
      classes: 'bg-amber-100 text-amber-800',
      dotColor: 'bg-amber-500',
    },
    [TaskMaterialStatus.RESERVED]: {
      label: tEnumLabels.taskMaterialStatusReserved,
      classes: 'bg-blue-100 text-blue-800',
      dotColor: 'bg-blue-500',
    },
    [TaskMaterialStatus.CONSUMED]: {
      label: tEnumLabels.taskMaterialStatusConsumed,
      classes: 'bg-green-100 text-green-800',
      dotColor: 'bg-green-500',
    },
    [TaskMaterialStatus.RETURNED]: {
      label: tEnumLabels.taskMaterialStatusReturned,
      classes: 'bg-gray-100 text-gray-800',
      dotColor: 'bg-gray-500',
    },
  };

  const config = statusConfig[status];
  if (!config) return null;

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-full font-medium whitespace-nowrap w-fit',
        sizeClasses[size],
        config.classes,
        className,
      )}
      title={config.label}>
      <span className={cn('w-2 h-2 rounded-full', config.dotColor)} />
      {config.label}
    </span>
  );
};
